/**
 * @fileoverview Navigation utility functions for path matching, item lookup,
 * and building navigation item to path mappings.
 */

import { match, type MatchFunction } from 'path-to-regexp';
import invariant from 'invariant';
import type { Navigation, NavigationItem, NavigationPageItem, NavigationSubheaderItem } from '../types';

/**
 * Navigation item kind type.
 */
type NavigationItemKind = 'page' | 'header' | 'divider';

/**
 * Gets the kind of a navigation item, defaulting to 'page' if unspecified.
 */
export function getItemKind(item: NavigationItem): NavigationItemKind {
  return item.kind ?? 'page';
}

/**
 * Type guard to check if a navigation item is a page item.
 */
export function isPageItem(item: NavigationItem): item is NavigationPageItem {
  return getItemKind(item) === 'page';
}

/**
 * Gets the display title for a navigation item.
 */
export function getItemTitle(item: NavigationPageItem | NavigationSubheaderItem): string {
  return isPageItem(item) ? (item.title ?? item.segment ?? '') : item.title;
}

/**
 * Builds a map of navigation page items to their respective paths. This map is used to quickly
 * lookup the path of a navigation item. It will be cached for the lifetime of the navigation.
 */
function buildItemToPathMap(navigation: Navigation): Map<NavigationPageItem, string> {
  const map = new Map<NavigationPageItem, string>();

  const visit = (item: NavigationItem, base: string) => {
    if (isPageItem(item)) {
      // Append segment to base path. Make sure to always have an initial slash, and slashes between segments.
      const path =
        `${base.startsWith('/') ? base : `/${base}`}${base && base !== '/' && item.segment ? '/' : ''}${item.segment || ''}` ||
        '/';
      map.set(item, path);
      if (item.children) {
        for (const child of item.children) {
          visit(child, path);
        }
      }
    }
  };

  for (const item of navigation) {
    visit(item, '');
  }

  return map;
}

const itemToPathMapCache = new WeakMap<Navigation, Map<NavigationPageItem, string>>();

/**
 * Gets the cached map of navigation page items to their respective paths.
 */
function getItemToPathMap(navigation: Navigation) {
  let map = itemToPathMapCache.get(navigation);
  if (!map) {
    map = buildItemToPathMap(navigation);
    itemToPathMapCache.set(navigation, map);
  }
  return map;
}

/**
 * Build a lookup map of paths to navigation items. This map is used to match paths against
 * to find the active page.
 */
function buildItemLookup(navigation: Navigation) {
  const map = new Map<string | MatchFunction<object>, NavigationPageItem>();
  const visit = (item: NavigationItem) => {
    if (isPageItem(item)) {
      const path = getItemPath(navigation, item);
      if (map.has(path)) {
        console.warn(`Duplicate path in navigation: ${path}`);
      }

      map.set(path, item);
      if (item.pattern) {
        const basePath = item.segment ? path.slice(0, -item.segment.length) : path;
        // Normalize path concatenation to avoid double slashes
        let patternPath: string;
        if (basePath.endsWith('/') && item.pattern.startsWith('/')) {
          // Both have slashes, remove one
          patternPath = basePath + item.pattern.slice(1);
        } else if (basePath.endsWith('/') || item.pattern.startsWith('/')) {
          // One has a slash, concatenate directly
          patternPath = basePath + item.pattern;
        } else {
          // Neither has a slash, add one
          patternPath = basePath + '/' + item.pattern;
        }
        map.set(match(patternPath), item);
      }
      if (item.children) {
        for (const child of item.children) {
          visit(child);
        }
      }
    }
  };
  for (const item of navigation) {
    visit(item);
  }
  return map;
}
const itemLookupMapCache = new WeakMap<Navigation, Map<string | MatchFunction<object>, NavigationPageItem>>();

/**
 * Gets the cached lookup map of paths to navigation items.
 */
function getItemLookup(navigation: Navigation): Map<string | MatchFunction<object>, NavigationPageItem> {
  let map = itemLookupMapCache.get(navigation);
  if (!map) {
    map = buildItemLookup(navigation);
    itemLookupMapCache.set(navigation, map);
  }
  return map;
}

/**
 * Matches a path against the navigation to find the active page. i.e. the page that should be
 * marked as selected in the navigation.
 */
export function matchPath(navigation: Navigation, path: string): NavigationPageItem | null {
  const lookup = getItemLookup(navigation);

  for (const [key, item] of lookup.entries()) {
    if (typeof key === 'string' && key === path) {
      return item;
    }
    if (typeof key === 'function' && key(path)) {
      return item;
    }
  }

  return null;
}

/**
 * Gets the path for a specific navigation page item.
 * If the item has a custom href, it takes precedence over the computed path.
 */
export function getItemPath(navigation: Navigation, item: NavigationPageItem): string {
  if (item.href) {
    return item.href;
  }
  const map = getItemToPathMap(navigation);
  const path = map.get(item);
  invariant(path, `Item not found in navigation: ${item.title}`);
  return path;
}

/**
 * Checks if a specific navigation page item has the active page as a child item.
 */
export function hasSelectedNavigationChildren(
  navigation: Navigation,
  item: NavigationPageItem,
  activePagePath: string,
): boolean {
  if (item.children) {
    return item.children.some((nestedItem) => {
      if (!isPageItem(nestedItem)) {
        return false;
      }

      if (nestedItem.children) {
        return hasSelectedNavigationChildren(navigation, nestedItem, activePagePath);
      }

      return activePagePath === getItemPath(navigation, nestedItem);
    });
  }

  return false;
}

